/**
 * Format currency amounts in Indian number format with abbreviations.
 *
 * Below 1,000      → as-is       (₹750)
 * 1,000–99,999     → thousands   (₹1.5K, ₹45.2K)
 * 1,00,000–99,99,999 → lakhs    (₹1.5L, ₹23.4L)
 * 1,00,00,000+     → crores      (₹1.2Cr, ₹5.8Cr)
 */

/** Half-up to paise. 37.485 → 37.49. 10.2 → 10.2. 152.364 → 152.36. */
export function roundPaise(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  const sign = n < 0 ? -1 : 1;
  return sign * (Math.round((Math.abs(n) + 1e-8) * 100) / 100);
}
const CRORE = 1_00_00_000;
const LAKH = 1_00_000;
const THOUSAND = 1_000;

export function formatCurrencyWhole(amount, symbol = '₹') {
  const parsed = parseNumericAmount(amount);
  const num = Math.round(parsed == null ? 0 : parsed);
  const sign = num < 0 ? '-' : '';
  return `${sign}${symbol}${Math.abs(num).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

/** Keep paise. 27.65 stays ₹27.65. Whole rupees stay ₹28 unless alwaysPaise. */
export function formatCurrencyPaise(amount, symbol = '₹', { alwaysPaise = false } = {}) {
  const parsed = parseNumericAmount(amount);
  const num = Math.round((parsed == null ? 0 : parsed) * 100) / 100;
  const sign = num < 0 ? '-' : '';
  const hasPaise = alwaysPaise || Math.abs(num - Math.round(num)) >= 0.005;
  return `${sign}${symbol}${Math.abs(num).toLocaleString('en-IN', {
    minimumFractionDigits: hasPaise ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

const COUNT_LABEL_RE = /\b(gcs?|qty|articles?|count|trips?|records?|shipments?)\s*$/i;
const RATE_LABEL_RE = /\b(rate|pct|%|km|kms|kilometres?|distance)\b/i;
const MONEY_LABEL_RE = /(\(rs\.?\)|₹|\b(amount|amt|freight|commission|tds|salary|settlement|revenue|outstanding|handling|charges?|collected|nrr|hire|balance|paid|payment|to-?pay|topay|credit|loading|unloading|docket|toll|packing|insurance|surcharge|pickup|delivery|late|discount|other|cost|net|total|interest|due|payables?|receivables?|expenses?|exp\b|gross|business|intransit|in.?transit|refund|profit|loss|value)\b)/i;

export function isFinancialColumnLabel(col) {
  const name = String(col || '').replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
  if (!name) return false;
  if (COUNT_LABEL_RE.test(name)) return false;
  if (RATE_LABEL_RE.test(name) && !/\bamount\b/i.test(name)) return false;
  if (/^gst$/i.test(name)) return true;
  return MONEY_LABEL_RE.test(name);
}

export function parseNumericAmount(value) {
  if (value == null || value === '' || value === '—') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const raw = String(value)
    .replace(/₹/g, '')
    .replace(/Rs\.?/gi, '')
    .replace(/,/g, '')
    .trim();
  if (!raw || !/^-?\d+(\.\d+)?$/.test(raw)) return null;
  const num = Number(raw);
  return Number.isFinite(num) ? num : null;
}

/** Display-only paise (2dp) for report / analytics table cells — matches settlement. */
export function formatFinancialCell(col, value, { withSymbol = false } = {}) {
  if (value == null || value === '') return value;
  if (!isFinancialColumnLabel(col)) return String(value);
  const num = parseNumericAmount(value);
  if (num == null) return String(value);
  return formatCurrencyPaise(num, withSymbol ? '₹' : '', { alwaysPaise: true });
}

export function formatCurrency(amount, symbol = '₹') {
  const num = parseFloat(amount) || 0;
  const abs = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  let formatted;
  if (abs >= CRORE) {
    formatted = `${(abs / CRORE).toFixed(1)}Cr`;
  } else if (abs >= LAKH) {
    formatted = `${(abs / LAKH).toFixed(1)}L`;
  } else if (abs >= THOUSAND) {
    formatted = `${(abs / THOUSAND).toFixed(1)}K`;
  } else {
    formatted = abs.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  return `${sign}${symbol}${formatted}`;
}

export function fullCurrency(amount, symbol = '₹') {
  const num = parseFloat(amount) || 0;
  return `${symbol}${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatReportCell(label, value, empty = '') {
  if (value == null || value === '') return empty;
  if (!isFinancialColumnLabel(label)) {
    return String(value);
  }
  const num = parseNumericAmount(value);
  if (num == null) return String(value);
  return formatCurrencyPaise(num, '₹', { alwaysPaise: true });
}

export function CurrencyDisplay({ amount, symbol = '₹', className = '' }) {
  return (
    <span className={className} title={fullCurrency(amount, symbol)}>
      {formatCurrency(amount, symbol)}
    </span>
  );
}

export default formatCurrency;
